import { useEffect, useMemo, useState } from "react";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  ShieldAlert, Loader2, Flag, UserX, History, Ban, ShieldCheck,
  Inbox, ChevronRight,
} from "lucide-react";
import { toast } from "sonner";

const TABS = [
  { id: "reports",    label: "Signalements", icon: Flag },
  { id: "audit",      label: "Historique",   icon: History },
];

const REPORT_STATUSES = [
  { value: "pending",   label: "En attente" },
  { value: "reviewed",  label: "Examiné" },
  { value: "dismissed", label: "Rejeté" },
  { value: "actioned",  label: "Action prise" },
];

const TARGET_LABELS = {
  user: "Utilisateur",
  creator: "Créateur",
  talent: "Talent",
  collab: "Collaboration",
  concours: "Concours",
  contest_entry: "Participation",
  portfolio: "Portfolio",
  other: "Autre",
};

const REASON_LABELS = {
  spam: "Spam",
  harassment: "Harcèlement",
  inappropriate_content: "Contenu inapproprié",
  fake_profile: "Faux profil",
  scam: "Arnaque",
  intellectual_property: "Droits d'auteur",
  minor: "Mineur",
  violence: "Violence",
  other: "Autre",
};

function resolveImage(p) {
  if (!p) return "";
  if (/^https?:\/\//i.test(p)) return p;
  return `${API}/files/${p}`;
}

export default function AdminModeration() {
  const [tab, setTab] = useState("reports");
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/admin/moderation/stats").then(({ data }) => setStats(data)).catch(() => setStats(null));
  }, []);

  return (
    <div data-testid="admin-moderation-page">
      <div className="mb-8">
        <p className="caption-label text-[#A26F3E]">Sécurité</p>
        <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl text-[#1A1A1A] mt-1 leading-[1.05]">Modération</h1>
        <p className="text-[#666] mt-2">Pilotez les signalements, les bannissements et l'historique des actions admin.</p>
      </div>

      {/* Stats strip */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6" data-testid="moderation-stats">
          <StatCard label="Signalements en attente" value={stats.reports_pending} accent="#B44F3F" />
          <StatCard label="Action prise"            value={stats.reports_actioned} accent="#3A4A1F" />
          <StatCard label="Rejetés"                 value={stats.reports_dismissed} accent="#8B8680" />
          <StatCard label="Bannis (perm.)"          value={stats.users_suspended} accent="#1A1A1A" />
          <StatCard label="Bannis (temp.)"          value={stats.users_banned_temporary} accent="#A26F3E" />
        </div>
      )}

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 mb-6" data-testid="moderation-tabs">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = t.id === tab;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-4 py-2 rounded-full text-sm border transition-all inline-flex items-center gap-2 ${
                active ? "bg-[#1A1A1A] text-white border-[#1A1A1A]" : "bg-white text-[#1A1A1A] border-[#EADDC9] hover:border-[#1A1A1A]"
              }`}
              data-testid={`mod-tab-${t.id}`}
            >
              <Icon className="w-4 h-4" />
              {t.label}
            </button>
          );
        })}
      </div>

      {tab === "reports" && <ReportsPanel onAction={() => {
        api.get("/admin/moderation/stats").then(({ data }) => setStats(data));
      }} />}
      {tab === "audit"   && <AuditPanel />}
    </div>
  );
}

function StatCard({ label, value, accent }) {
  return (
    <div className="card-lux p-4">
      <p className="font-serif text-3xl text-[#1A1A1A] leading-none" style={{ color: accent }}>{value ?? "—"}</p>
      <p className="text-[10px] uppercase tracking-wider text-[#8B8680] mt-2">{label}</p>
    </div>
  );
}

/* ============================== Reports Panel ============================== */
function ReportsPanel({ onAction }) {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [actionDialog, setActionDialog] = useState(null); // { report, kind: "ban"|"unban"|"dismiss" }

  const fetchReports = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/reports", { params: { status: statusFilter, limit: 100 } });
      setReports(Array.isArray(data) ? data : []);
    } catch {
      setReports([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchReports(); /* eslint-disable-next-line */ }, [statusFilter]);

  const dismiss = async (r) => {
    try {
      await api.patch(`/admin/reports/${r.id}`, { status: "dismissed" });
      toast.success("Signalement rejeté");
      fetchReports();
      onAction?.();
    } catch (e) { toast.error(e?.userMessage || "Échec"); }
  };

  const markReviewed = async (r) => {
    try {
      await api.patch(`/admin/reports/${r.id}`, { status: "reviewed" });
      toast.success("Marqué comme examiné");
      fetchReports();
      onAction?.();
    } catch (e) { toast.error(e?.userMessage || "Échec"); }
  };

  return (
    <div data-testid="reports-panel">
      <div className="flex items-center justify-between mb-4">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="rounded-full w-56" data-testid="reports-status-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les statuts</SelectItem>
            {REPORT_STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="py-16 flex items-center justify-center"><Loader2 className="w-6 h-6 text-[#C68E59] animate-spin" /></div>
      ) : reports.length === 0 ? (
        <div className="card-lux p-12 text-center" data-testid="reports-empty">
          <ShieldCheck className="w-8 h-8 text-[#7A8E3F] mx-auto" />
          <p className="font-serif text-xl text-[#1A1A1A] mt-3">Aucun signalement</p>
          <p className="text-sm text-[#666] mt-1">La communauté Helencia est propre — bravo !</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="reports-list">
          {reports.map((r) => (
            <ReportRow
              key={r.id}
              report={r}
              onDismiss={() => dismiss(r)}
              onMarkReviewed={() => markReviewed(r)}
              onBan={() => setActionDialog({ report: r, kind: "ban" })}
            />
          ))}
        </div>
      )}

      <BanDialog
        open={!!actionDialog && actionDialog.kind === "ban"}
        report={actionDialog?.report}
        onClose={() => setActionDialog(null)}
        onDone={() => { setActionDialog(null); fetchReports(); onAction?.(); }}
      />
    </div>
  );
}

function ReportRow({ report, onDismiss, onMarkReviewed, onBan }) {
  const r = report;
  const t = r.target_preview || {};
  const img = t.picture || t.logo_url || t.image_url || t.cover_image;
  const title = t.name || t.company_name || t.title || `${TARGET_LABELS[r.target_type] || r.target_type} ${r.target_id.slice(0, 8)}`;

  const statusStyles = {
    pending:   { bg: "#FAF3EA", text: "#A26F3E" },
    reviewed:  { bg: "#E5E5F0", text: "#3F4B7A" },
    dismissed: { bg: "#EADDC9", text: "#666" },
    actioned:  { bg: "#E9EFD7", text: "#3A4A1F" },
  }[r.status] || { bg: "#FAF3EA", text: "#A26F3E" };

  return (
    <div className="card-lux p-5" data-testid={`report-row-${r.id}`}>
      <div className="flex items-start gap-4">
        {img ? (
          <img src={resolveImage(img)} alt="" className="w-14 h-14 rounded-xl object-cover border border-[#EADDC9]" />
        ) : (
          <div className="w-14 h-14 rounded-xl bg-[#FAF3EA] flex items-center justify-center text-[#C68E59] border border-[#EADDC9]">
            <Flag className="w-5 h-5" />
          </div>
        )}

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full" style={{ backgroundColor: statusStyles.bg, color: statusStyles.text }}>
              {REPORT_STATUSES.find((s) => s.value === r.status)?.label || r.status}
            </span>
            <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full bg-[#1A1A1A] text-white">
              {TARGET_LABELS[r.target_type] || r.target_type}
            </span>
            <span className="text-[10px] uppercase tracking-wider text-[#B44F3F]">
              {REASON_LABELS[r.reason] || r.reason}
            </span>
          </div>
          <p className="font-serif text-base text-[#1A1A1A] mt-1.5 truncate">{title}</p>
          {r.description && <p className="text-sm text-[#666] mt-1 line-clamp-2">{r.description}</p>}
          <div className="flex items-center gap-2 mt-2 text-[11px] text-[#8B8680]">
            <span>par {r.reporter_name || r.reporter_email}</span>
            <span>·</span>
            <span>{new Date(r.created_at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}</span>
          </div>
        </div>

        {r.status === "pending" && (
          <div className="flex flex-col gap-2 flex-shrink-0">
            <Button size="sm" variant="outline" onClick={onMarkReviewed} className="rounded-full text-xs h-8 px-3" data-testid={`mark-reviewed-${r.id}`}>
              Marquer examiné
            </Button>
            <Button size="sm" variant="outline" onClick={onDismiss} className="rounded-full text-xs h-8 px-3 border-[#EADDC9] text-[#666]" data-testid={`dismiss-${r.id}`}>
              Rejeter
            </Button>
            {(r.target_type === "user" || r.target_type === "creator" || r.target_type === "talent") && (
              <Button size="sm" onClick={onBan} className="rounded-full text-xs h-8 px-3 bg-[#B44F3F] hover:bg-[#8C3D2E] text-white" data-testid={`ban-${r.id}`}>
                <Ban className="w-3 h-3 mr-1" /> Bannir
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function BanDialog({ open, report, onClose, onDone }) {
  const [duration, setDuration] = useState("7");  // "1" | "7" | "30" | "perm"
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) { setDuration("7"); setReason(""); }
  }, [open]);

  if (!report) return null;

  const submit = async () => {
    setBusy(true);
    try {
      const payload = {
        duration_days: duration === "perm" ? null : parseInt(duration, 10),
        reason,
        related_report_id: report.id,
      };
      await api.post(`/admin/users/${report.target_id}/ban`, payload);
      toast.success(duration === "perm" ? "Utilisateur banni définitivement" : `Utilisateur banni ${duration} jour(s)`);
      onDone?.();
    } catch (e) {
      toast.error(e?.userMessage || "Échec du bannissement");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="ban-dialog">
        <DialogHeader><DialogTitle>Bannir l'utilisateur</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-[#666]">
            Le bannissement déconnecte immédiatement l'utilisateur et bloque sa reconnexion. Le signalement sera marqué comme "Action prise".
          </p>
          <div>
            <Label className="caption-label">Durée</Label>
            <Select value={duration} onValueChange={setDuration}>
              <SelectTrigger className="rounded-xl mt-2" data-testid="ban-duration-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="1">24 heures</SelectItem>
                <SelectItem value="7">7 jours</SelectItem>
                <SelectItem value="30">30 jours</SelectItem>
                <SelectItem value="perm">Permanent</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="caption-label">Raison (visible dans les logs)</Label>
            <Textarea
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Spam répété, harcèlement, etc."
              className="rounded-xl mt-2"
              data-testid="ban-reason-input"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy} className="rounded-full">Annuler</Button>
          <Button onClick={submit} disabled={busy} className="rounded-full bg-[#B44F3F] hover:bg-[#8C3D2E] text-white" data-testid="ban-confirm">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Ban className="w-4 h-4 mr-2" />}
            Confirmer le bannissement
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ============================== Audit Panel ============================== */
const ACTION_LABELS = {
  ban_temporary: { label: "Ban temporaire",   color: "#B44F3F" },
  ban_permanent: { label: "Ban permanent",    color: "#1A1A1A" },
  unban_user:    { label: "Débannissement",   color: "#7A8E3F" },
  suspend_user:  { label: "Suspension",       color: "#B44F3F" },
  reactivate_user:{ label: "Réactivation",     color: "#7A8E3F" },
  verify_pro:    { label: "Vérification pro", color: "#3F4B7A" },
  unverify_pro:  { label: "Retrait vérif.",   color: "#8B8680" },
  report_dismissed: { label: "Signalement rejeté",  color: "#8B8680" },
  report_reviewed:  { label: "Signalement examiné", color: "#3F4B7A" },
  report_actioned:  { label: "Signalement traité",  color: "#3A4A1F" },
};

function AuditPanel() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionFilter, setActionFilter] = useState("all");

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (actionFilter !== "all") params.action = actionFilter;
      const { data } = await api.get("/admin/audit-log", { params });
      setLogs(Array.isArray(data) ? data : []);
    } catch {
      setLogs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchLogs(); /* eslint-disable-next-line */ }, [actionFilter]);

  const actionOptions = useMemo(
    () => [{ value: "all", label: "Toutes les actions" }, ...Object.entries(ACTION_LABELS).map(([v, m]) => ({ value: v, label: m.label }))],
    []
  );

  return (
    <div data-testid="audit-panel">
      <div className="flex items-center justify-between mb-4">
        <Select value={actionFilter} onValueChange={setActionFilter}>
          <SelectTrigger className="rounded-full w-72" data-testid="audit-action-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {actionOptions.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="py-16 flex items-center justify-center"><Loader2 className="w-6 h-6 text-[#C68E59] animate-spin" /></div>
      ) : logs.length === 0 ? (
        <div className="card-lux p-12 text-center" data-testid="audit-empty">
          <Inbox className="w-8 h-8 text-[#C68E59] mx-auto" />
          <p className="font-serif text-xl text-[#1A1A1A] mt-3">Aucun journal d'action</p>
        </div>
      ) : (
        <div className="card-lux p-2 overflow-hidden" data-testid="audit-list">
          <div className="divide-y divide-[#FAF3EA]">
            {logs.map((l) => {
              const meta = ACTION_LABELS[l.action] || { label: l.action, color: "#8B8680" };
              return (
                <div key={l.id} className="flex items-center gap-4 px-4 py-3" data-testid={`audit-row-${l.id}`}>
                  <div
                    className="w-2 h-2 rounded-full flex-shrink-0"
                    style={{ backgroundColor: meta.color }}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-semibold" style={{ color: meta.color }}>{meta.label}</span>
                      {l.target_type && (
                        <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#FAF3EA] text-[#666]">
                          {l.target_type}
                        </span>
                      )}
                      {l.target_id && (
                        <span className="text-[11px] text-[#8B8680] font-mono">{l.target_id.slice(0, 12)}</span>
                      )}
                    </div>
                    {l.details?.reason && (
                      <p className="text-xs text-[#666] mt-1 line-clamp-1">« {l.details.reason} »</p>
                    )}
                    {l.details?.duration_days && (
                      <p className="text-[11px] text-[#8B8680] mt-0.5">{l.details.duration_days} jour(s)</p>
                    )}
                  </div>
                  <div className="text-right text-[11px] text-[#8B8680] flex-shrink-0">
                    <p>{l.admin_name || l.admin_email || "—"}</p>
                    <p>{new Date(l.created_at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
